/**
 * Report Builder Response Processor
 *
 * Specialized processor for Report Builder agent.
 * Handles preview actions and conversation quality tracking.
 *
 * @module services/responseProcessors/ReportBuilderProcessor
 */

import {
  IResponseProcessor,
  NormalizedResponse,
  ProcessingResult,
  ProcessorContext,
  ChatMessage,
} from './types';
import { ChatMessageResponse } from '@/collections/chat';
import { executeAgentAction } from '@/config/agentActions';
import { DefaultProcessor } from './DefaultProcessor';

/**
 * Message structure within a reply
 */
interface ReplyMessage {
  content?: string;
  [key: string]: unknown;
}

/**
 * Reply object structure with messages array
 */
interface ReplyWithMessages {
  messages?: ReplyMessage[];
  [key: string]: unknown;
}

/**
 * Type guard to check if reply has a messages array
 */
function hasMessagesArray(reply: unknown): reply is ReplyWithMessages {
  return (
    typeof reply === 'object' &&
    reply !== null &&
    'messages' in reply &&
    Array.isArray((reply as ReplyWithMessages).messages)
  );
}

/**
 * Type guard to check if a message has valid string content
 */
function hasStringContent(msg: ReplyMessage): msg is ReplyMessage & { content: string } {
  return typeof msg.content === 'string';
}

/**
 * Parameters for report preview action
 * Properties are optional as they may come from different sources with varying completeness
 */
interface ReportPreviewParams {
  parameters?: Record<string, unknown>;
  report_name?: string;
  confidence_score?: number;
  suggested_questions?: string[];
  sample_data?: unknown;
  is_sample_data?: boolean;
  [key: string]: unknown;
}

/**
 * Processor for Report Builder agent
 *
 * Handles:
 * - Preview action detection from metadata and events
 * - Auto-navigation to /assistant/ai-report-builder page
 * - Conversation quality tracking analytics
 * - JSON control message filtering
 */
export class ReportBuilderProcessor implements IResponseProcessor {
  readonly agentIds = ['report-builder'];
  readonly description = 'Processor for Report Builder agent with preview and analytics';

  /** Phrases indicating a clarification request from the assistant */
  private static readonly CLARIFICATION_PHRASES: readonly string[] = [
    'could you clarify',
    'please clarify',
    'need more information',
    'can you be more specific',
    'could you provide more details',
    'what do you mean by',
    'which',
    'what type of',
  ];

  private defaultProcessor: IResponseProcessor;
  private pendingTimeouts: number[] = [];

  /**
   * Create a ReportBuilderProcessor
   * @param defaultProcessor - Optional processor for base normalization (defaults to DefaultProcessor)
   */
  constructor(defaultProcessor?: IResponseProcessor) {
    this.defaultProcessor = defaultProcessor ?? new DefaultProcessor();
  }

  /**
   * Normalize response with Report Builder specific filtering
   */
  normalizeResponse(response: ChatMessageResponse): NormalizedResponse {
    // Use default normalization as base
    const normalized = this.defaultProcessor.normalizeResponse(response);

    // Additional filtering for Report Builder - remove JSON control messages
    if (normalized.isNewFormat && hasMessagesArray(response.reply)) {
      const messages: ReplyMessage[] = response.reply.messages ?? [];

      const filteredContent = messages
        .filter((msg): msg is ReplyMessage & { content: string } => {
          if (!hasStringContent(msg)) return false;

          // Try to parse as JSON - if it's a control message, filter it out
          try {
            const parsed = JSON.parse(msg.content);
            // Filter out objects that look like control messages
            if (parsed.action || parsed.parameters) return false;
            if (typeof parsed === 'object' && parsed !== null) return false;
          } catch (e) {
            // Not JSON - keep it
          }
          return true;
        })
        .map((msg) => msg.content);

      normalized.messageContent = filteredContent.join('\n\n');
    }

    // Clean up handoff signals
    normalized.messageContent = normalized.messageContent
      .replace(/\[HANDOFF_?SIGNAL\]/g, '')
      .trim();

    return normalized;
  }

  /**
   * Process response with Report Builder specific logic
   */
  async processResponse(
    normalized: NormalizedResponse,
    context: ProcessorContext
  ): Promise<ProcessingResult> {
    const result: ProcessingResult = {
      messagesToAdd: [],
      messagesToRemove: [],
      eventBusEmits: [],
      success: true,
    };

    // Create assistant message
    if (normalized.messageContent.trim()) {
      const assistantMessage: ChatMessage = {
        id: context.generateMessageId(),
        content: normalized.messageContent,
        timestamp: new Date(),
        isUser: false,
        agent: {
          name: context.selectedAgent.name,
          avatar: context.selectedAgent.avatar,
        },
        isStreaming: false,
      };
      result.messagesToAdd.push(assistantMessage);
    }

    // Check for preview action in events (new format)
    if (normalized.isNewFormat) {
      const previewEvent = normalized.events.find(
        (e) => e.type === 'preview_canvas_action'
      );
      if (previewEvent?.metadata?.action === 'show_report_preview') {
        const payload = previewEvent.metadata.payload as Record<string, any> | undefined;
        await this.handleReportPreview(
          payload?.extracted_parameters || payload,
          context,
          result
        );
      }
    }

    // Check for preview action in metadata (old format)
    if (normalized.metadata.action === 'show_report_preview') {
      const extractedParams =
        normalized.metadata.payload?.extracted_parameters || normalized.metadata;
      await this.handleReportPreview(extractedParams, context, result);
    } else if (
      normalized.metadata.parameters ||
      normalized.metadata.extracted_parameters ||
      normalized.metadata.measure
    ) {
      // Legacy format without explicit action
      const extractedParams =
        normalized.metadata.extracted_parameters ||
        normalized.metadata.payload?.extracted_parameters ||
        normalized.metadata;

      const reportPayload = {
        parameters:
          extractedParams.parameters ||
          normalized.metadata.parameters ||
          normalized.metadata,
        report_name: extractedParams.report_name || normalized.metadata.report_name,
        confidence_score:
          extractedParams.confidence_score || normalized.metadata.confidence_score,
        suggested_questions:
          extractedParams.suggested_questions ||
          normalized.metadata.suggested_questions,
        sample_data: extractedParams.sample_data,
        is_sample_data: extractedParams.is_sample_data,
        success: true,
      };

      result.eventBusEmits!.push({
        event: 'report-builder:preview',
        payload: reportPayload,
      });

      this.trackConversationQuality(context, result);
    }

    return result;
  }

  /**
   * Schedule agent action execution with timeout management
   */
  private scheduleAgentAction(
    params: ReportPreviewParams,
    context: ProcessorContext,
    delay: number
  ): void {
    const timeoutId = window.setTimeout(() => {
      this.removeTimeout(timeoutId);
      executeAgentAction(
        'show_report_preview',
        { extracted_parameters: params },
        {
          sessionId: context.conversationId,
          agentId: context.selectedAgent.id,
          userId: context.userId,
        }
      );
    }, delay);
    this.pendingTimeouts.push(timeoutId);
  }

  /**
   * Handle report preview action - navigate if needed and execute action
   */
  private async handleReportPreview(
    params: ReportPreviewParams,
    context: ProcessorContext,
    result: ProcessingResult
  ): Promise<void> {
    const isOnReportBuilderPage =
      context.vueInstance.$route.path === '/assistant/ai-report-builder' ||
      context.vueInstance.$route.name === 'ReportBuilder';

    if (!isOnReportBuilderPage) {
      // Navigate to Report Builder page
      await context.vueInstance.$router.push('/assistant/ai-report-builder');
      await context.vueInstance.$nextTick();

      // Re-emit chatbox:opened event after navigation
      result.eventBusEmits!.push({
        event: 'chatbox:opened',
        payload: {},
      });

      // Execute action after navigation with longer delay
      this.scheduleAgentAction(params, context, 300);
    } else {
      // Already on Report Builder page - execute action with short delay
      this.scheduleAgentAction(params, context, 100);
    }

    this.trackConversationQuality(context, result);
  }

  /**
   * Track conversation quality metrics for analytics
   */
  private trackConversationQuality(
    context: ProcessorContext,
    result: ProcessingResult
  ): void {
    const userMessages = context.currentConversation.filter((m) => m.isUser);
    const clarificationMessages = context.currentConversation.filter((m) =>
      this.isClarificationRequest(m)
    );

    result.eventBusEmits!.push({
      event: 'report-builder:conversation-quality',
      payload: {
        agentId: context.selectedAgent.id,
        sessionId: context.conversationId,
        messageCount: context.currentConversation.length,
        clarificationRequestsCount: clarificationMessages.length,
        successfulInterpretation: true,
        conversationDuration: context.conversationStartTime
          ? (Date.now() - context.conversationStartTime) / 1000
          : 0,
        queryRefinementIterations: Math.max(0, userMessages.length - 1),
      },
    });
  }

  /**
   * Check if a message is a clarification request from the assistant
   */
  private isClarificationRequest(message: ChatMessage): boolean {
    if (message.isUser) return false;

    const content = message.content.toLowerCase();
    return ReportBuilderProcessor.CLARIFICATION_PHRASES.some((phrase) => content.includes(phrase));
  }

  /**
   * Remove a timeout ID from the pending list after it executes
   */
  private removeTimeout(timeoutId: number): void {
    const index = this.pendingTimeouts.indexOf(timeoutId);
    if (index > -1) {
      this.pendingTimeouts.splice(index, 1);
    }
  }

  /**
   * Clean up all pending timeouts to prevent execution after teardown
   * Call this method when the component/context is being destroyed
   */
  public cleanup(): void {
    for (const timeoutId of this.pendingTimeouts) {
      window.clearTimeout(timeoutId);
    }
    this.pendingTimeouts = [];
  }
}
